import { Injectable, inject } from '@angular/core';
import {
  WatchQueryFetchPolicy,
  ObservableQuery,
  ApolloLink,
} from '@apollo/client';
import {
  CopyProjectRequest,
  FindAllProjectMembersRequest,
  FindAllProjectPermissionsRequest,
  FindUniqueProjectRequest,
  Project,
  ProjectMember,
  ProjectPermission,
  FindUniqueProjectRoleRequest,
  ProjectRole,
  UpdateProjectRequest,
  FindUniqueProjectRolePermissionsRequest,
} from '@ih/app/shared/apis/interfaces';
import { Store } from '@ngxs/store';
import { Apollo, gql } from 'apollo-angular';
import { catchError, map, Observable, of, take } from 'rxjs';

@Injectable()
export class ProjectsService {
  private readonly apollo = inject(Apollo);
  private readonly store = inject(Store);

  getBaseProjectRole(
    request: FindUniqueProjectRoleRequest,
  ): Observable<ProjectRole> {
    type ResultType = { findUniqueProjectRole: ProjectRole };
    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query findUniqueProjectRole($name: String, $projectId: String) {
        findUniqueProjectRole(request: { name: $name, projectId: $projectId }) {
          id
          name
          projectRolePermissions {
            id
            projectPermissionId
          }
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          name: request.name,
          projectId: request.projectId,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
              variables: {
                name: request.name,
                projectId: request.projectId,
              },
            });
          }
          return result.data?.findUniqueProjectRole as
            | ProjectRole
            | undefined as ProjectRole;
        }),
      );
  }
  //ProjectMembers
  findMeProjectMemberWithRolePermissions(
    request: FindAllProjectMembersRequest,
  ): Observable<ProjectMember[]> {
    type ResultType = { findMeProjectMember: ProjectMember[] | null };
    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query FindMeProjectMember($request: FindAllProjectMembersRequest!) {
        findMeProjectMember(request: $request) {
          role
          projectId
          projectRoleId
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
              variables: {
                request,
              },
            });
          }
          return (
            (result.data?.findMeProjectMember as ProjectMember[] | undefined) ||
            []
          );
        }),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  findProjectRolePermissions(
    request: FindUniqueProjectRolePermissionsRequest,
  ): Observable<ProjectPermission[]> {
    type ResultType = { findUniqueProjectRolePermissions: ProjectPermission[] };

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query FindRolePermissions(
        $request: FindUniqueProjectRolePermissionsRequest!
      ) {
        findUniqueProjectRolePermissions(request: $request) {
          id
          identifier
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
              variables: {
                request,
              },
            });
          }
          return (
            (result.data?.findUniqueProjectRolePermissions as
              | ProjectPermission[]
              | undefined) || []
          );
        }),
        catchError((error) => {
          console.error('error', error);
          return of([]);
        }),
      );
  }

  //ProjectMembers
  findAllProjectPermissions(
    request: FindAllProjectPermissionsRequest,
  ): Observable<ProjectPermission[]> {
    type ResultType = { findAllProjectPermissions: ProjectPermission[] | null };
    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query FindAllProjectPermissions(
        $request: FindAllProjectPermissionsRequest!
      ) {
        findAllProjectPermissions(request: $request) {
          id
          identifier
        }
      }
    `;

    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
              variables: {
                request,
              },
            });
          }
          return (
            (result.data?.findAllProjectPermissions as
              | ProjectPermission[]
              | undefined) || []
          );
        }),
      );
  }

  // Projects
  findUniqueProject(
    request: FindUniqueProjectRequest,
  ): Observable<Project | null> {
    type ResultType = { findUniqueProject: Project | null };

    let fetchPolicy = 'cache-only';

    const networkStatus = this.store.selectSnapshot<boolean>(
      (state) => state.network.status,
    );

    if (networkStatus) {
      fetchPolicy = 'network-only';
    }

    const query = gql`
      query findUniqueProject($request: FindUniqueProjectRequest!) {
        findUniqueProject(request: $request) {
          name
          id
          template
        }
      }
    `;
    return this.apollo
      .watchQuery<ResultType>({
        query,
        variables: {
          request,
        },
        fetchPolicy: <WatchQueryFetchPolicy>fetchPolicy,
      })
      .valueChanges.pipe(
        take(1),
        map((result: ObservableQuery.Result<ResultType>) => {
          if (result.data && networkStatus) {
            this.apollo.client.cache.writeQuery({
              query,
              data: result.data,
              variables: {
                request,
              },
            });
          }
          return (
            (result.data?.findUniqueProject as Project | null | undefined) ||
            null
          );
        }),
        catchError(() => of(null)),
      );
  }

  copyProject(
    request: CopyProjectRequest,
  ): Observable<Apollo.MutateResult<any>> {
    const query = gql`
      mutation copyProject($request: CopyProjectRequest!) {
        copyProject(request: $request) {
          id
          name
        }
      }
    `;

    return this.apollo.mutate({
      mutation: query,
      variables: {
        request,
      },
    });
  }

  updateProject(
    request: UpdateProjectRequest,
  ): Observable<Apollo.MutateResult<any>> {
    const query = gql`
      mutation UpdateProject($request: UpdateProjectRequest!) {
        updateProject(request: $request) {
          id
          name
        }
      }
    `;

    return this.apollo.mutate({
      mutation: query,
      variables: {
        request,
      },
    });
  }
}
